import React, {useState, useRef, useEffect} from 'react'
import {Box, Flex, useMultiStyleConfig} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import ChevronDown from '../../icons/shared/arrows/ChevronDown'
import {useIntl} from 'react-intl'

const YearSelect = ({year, years, onChange}) => {
    const styles = useMultiStyleConfig('DatePicker', {})
    const [isOpen, setIsOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(() => Math.max(years.indexOf(year), 0))
    const toggleOpen = () => setIsOpen((v) => !v)
    const listboxRef = useRef(null)
    const {formatMessage} = useIntl()

    useEffect(() => {
        const index = years.indexOf(year)
        setActiveIndex(index >= 0 ? index : 0)
    }, [year, years])

    useEffect(() => {
        if (isOpen && listboxRef.current) {
            const activeOption = listboxRef.current.querySelector(
                `#year-option-${years[activeIndex]}`,
            )
            if (activeOption) {
                activeOption.scrollIntoView({block: 'nearest', behavior: 'smooth'})
            }
        }
    }, [activeIndex, isOpen, years])

    return (
        <Box {...styles?.yearSelectContainer}>
            <Flex
                role="combobox"
                aria-label={formatMessage({
                    id: 'date_picker_header.year_select',
                    defaultMessage: 'Year Selection',
                })}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-controls="year-select-listbox"
                aria-autocomplete="none"
                aria-activedescendant={isOpen ? `year-option-${years[activeIndex]}` : undefined}
                tabIndex={0}
                onClick={toggleOpen}
                {...styles?.yearCombobox}
                onKeyDown={(e) => {
                    switch (e.key) {
                        case 'Enter':
                        case ' ':
                            e.preventDefault()
                            if (isOpen) {
                                onChange(years[activeIndex])
                                setIsOpen(false)
                            } else {
                                setIsOpen(true)
                            }
                            break
                        case 'ArrowDown':
                            e.preventDefault()
                            setIsOpen(true)
                            setActiveIndex((i) => Math.min(i + 1, years.length - 1))
                            break
                        case 'ArrowUp':
                            e.preventDefault()
                            setIsOpen(true)
                            setActiveIndex((i) => Math.max(i - 1, 0))
                            break
                        case 'Escape':
                            e.preventDefault()
                            setIsOpen(false)
                            break

                        case 'Tab':
                            setIsOpen(false)
                            break
                        default:
                            break
                    }
                }}
            >
                <Box {...styles?.yearText}>{year}</Box>
                <Box
                    aria-hidden="true"
                    transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                    {...styles?.yearChevronContainer}
                >
                    <ChevronDown color="black" strokeWidth={2.5} {...styles?.yearChevronIcon} />
                </Box>
            </Flex>

            {isOpen && (
                <Box
                    ref={listboxRef}
                    id="year-select-listbox"
                    role="listbox"
                    {...styles?.yearListbox}
                >
                    {years.map((y, index) => (
                        <Box
                            key={y}
                            id={`year-option-${y}`}
                            role="option"
                            aria-selected={y === year}
                            bg={index === activeIndex ? 'background.light' : 'transparent'}
                            onClick={() => {
                                onChange(y)
                                setIsOpen(false)
                            }}
                            onMouseEnter={() => setActiveIndex(index)}
                            {...styles?.yearOption}
                        >
                            {y}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

YearSelect.propTypes = {
    year: PropTypes.number,
    years: PropTypes.array,
    onChange: PropTypes.func,
}

export default YearSelect
